import { ImageGrid } from "components";
import { ProjectStatus } from "models";
import { EmployeeRoles } from "models/Employee";
import { DEFAULT_FIELD_VALUE } from "models/Model";
import type { FlattenModelField, ModelInstance, PlainObject } from "models/Model";
import { Role } from "models/Role";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Dropdown, Form } from "semantic-ui-react";
import { useAuth } from "services";
import { capitalize, is, logger, useForceUpdate } from "utils";
import { Checkbox } from "./forms/Checkbox";
import { MutatableField } from "./forms/MutatableField";
import { NumberInput } from "./forms/Number";
import { Radio } from "./forms/Radio";
import { ReadonlyField } from "./forms/Readonly";
import { Toggle } from "./forms/Toggle";
import { useT } from "i18n";

// Constants moved to use translations

/**
 * @todo we need to unify the types coming into this function
 * @param prop
 * @param values
 */
export const getValues = ({
  prop,
  values,
}: {
  prop: string;
  values?: object | string | string[] | undefined;
}): string[] => {
  if (values) {
    if (Array.isArray(values)) {
      return values;
    } else if (typeof values === "string") {
      return values.split("|");
    } else if (typeof values === "object") {
      return Object.values(values);
    } else {
      console.error(`Invalid 'values' definition in prop '${prop}'`);
    }
  }

  return [];
};

type FieldProps<T extends PlainObject> = {
  groupId: string;
  model: ModelInstance<T>;
  field: FlattenModelField;
  disabled?: boolean;
  readonly?: boolean;
};

export default function Field<T extends PlainObject>({
  groupId,
  model,
  field,
  disabled = false,
  readonly = false,
}: FieldProps<T>) {
  const t = useT();
  const forceUpdate = useForceUpdate();
  const { user } = useAuth();

  const {
    prop,
    label,
    type,
    required,
    html,
    activated,
    activatedBy,
    filteredBy,
    fixedValues,
    defaultValue,
    nullValue,
    isNull,
    placeholder,
  } = field;

  // ensure safe valueDisplay
  let valueDisplay: (value: string) => string;
  if (field.valueDisplay) {
    valueDisplay = (value) => {
      try {
        // @ts-expect-error - valueDisplay is a dynamic function that may not exist on all field types
        return field.valueDisplay(value);
      } catch (err) {
        logger.error(`Error in valueDisplay of prop '${prop}': %o`, err as object);
        return t("component.field.errorInValueDisplay", "(Oops, this is an error)");
      }
    };
  }

  let input: JSX.Element;
  const filterFields = is.string(filteredBy)
    ? (filteredBy as string).split("|")
    : (filteredBy as string[]);

  // Translate common model labels
  const translatedLabel = (() => {
    const labelMap: Record<string, string> = {
      Name: t("component.exportProjects.name", "Name"),
      Company: t("component.exportDealers.company", "Company"),
      Orders: t("component.exportProjects.orders", "Orders"),
      Quotes: t("component.exportProjects.quotes", "Quotes"),
      "Created Date": t("component.exportProjects.createdDate", "Created Date"),
      Street: t("component.exportDealers.street", "Street"),
      "Street 2": t("component.exportDealers.street2", "Street 2"),
      "One to Many Profile Setup": t(
        "section.admin.oneToManyProfileSetup",
        "One to Many Profile Setup",
      ),
      "Primary Contact Name": t(
        "component.exportProjects.primaryContactName",
        "Primary Contact Name",
      ),
    };
    return labelMap[label] || label;
  })();

  const labelEl = html ? (
    <label dangerouslySetInnerHTML={{ __html: translatedLabel }} />
  ) : (
    <label>{translatedLabel}</label>
  );

  let currentValue = isNull ? nullValue : model.get(prop);

  if (!currentValue && !isNull && defaultValue) {
    model.set(prop, (currentValue = defaultValue), groupId);
  }

  // support relative prop paths
  const resolvePath = (path: string) =>
    path.includes(".") ? path : prop.replace(/[\w]+$/, path);
  const getFieldValue = (path: string) => model.get(resolvePath(path));

  /**
   * @todo fix the types on this
   * @param value
   */
  const getDisplayValue = (value: string) => {
    if (
      value === EmployeeRoles.BUYER ||
      value === EmployeeRoles.ADMIN ||
      value === EmployeeRoles.TECHNICIAN ||
      value === EmployeeRoles.DESIGN
    ) {
      return capitalize(value);
    } else if (value === EmployeeRoles.CUST_SERV) {
      return t("component.field.customerService", "Customer Service");
    } else if (value === EmployeeRoles.SALES) {
      return t("component.field.lathamSales", "Latham Sales");
    } else if (value === EmployeeRoles.MEASURE_LITE) {
      return t("component.field.measureLite", "Measure Lite");
    }
    if (nullValue && (!value || value === nullValue)) {
      return nullValue;
    } else if (!!valueDisplay && value) {
      return valueDisplay(value);
    } else if (is.boolean(value)) {
      return value ? t("component.common.yes", "Yes") : t("component.common.no", "No");
    }
    return value;
  };

  const checkIfActive = useCallback(
    (initialized: boolean) => {
      const active =
        !activatedBy ||
        Object.entries(activatedBy).reduce((acc: boolean, [field, values]) => {
          const fieldValue = getFieldValue(field);

          return (
            acc &&
            (is.boolean(values)
              ? values === !!fieldValue
              : is.number(values)
                ? values === fieldValue
                : // NOTE: we must split the pipe string bc all strings include the empty string (which is the empty field value)
                  is.string(values)
                  ? (values as string).split("|").includes(fieldValue)
                  : is.array(values)
                    ? (values as any[]).includes(fieldValue)
                    : (console.warn(
                        `Invalid 'activatedBy' definition in prop '${prop}'`,
                      ),
                      false))
          );
        }, true);

      if (!active && currentValue) {
        // field became inactive, reset prop value
        model.set(prop, null, groupId);
      }

      if (initialized) {
        // set the component state
        setIsActive(active);
        // and also the field flag (for model.isValid() to pick it up)
        field.isActive = active;
      }

      return active;
    },
    [model, currentValue, forceUpdate],
  );

  const onChange = useCallback(
    (e: any, data: any) => {
      model.set(data.name, data.value, groupId);
      forceUpdate();
    },
    [model],
  );

  const toggleOn = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const active = e.target.checked;
      if (active) {
        // UI update workaround for certain glitchy types
        if (type === "number") {
          onChange(null, {
            name: prop,
            value: defaultValue || DEFAULT_FIELD_VALUE.number,
          });
        }
      } else {
        // reset prop value
        model.set(prop, null, groupId);
      }
    },
    [model, onChange],
  );

  const populateValues = useCallback(
    (initialized: boolean) => {
      let updatedValues = getValues(field);
      if (filterFields) {
        if (is.object(field.values)) {
          // it's a map filter
          updatedValues = (field.values as any)[getFieldValue(filterFields[0])] ?? [];
        } else {
          // it's a text filter
          const includesAllFilters = (value: string) =>
            filterFields.reduce(
              // NOTE: we must use an unlikely fallback prop value here because
              // all strings include the empty string (which is the empty field value)
              (acc: boolean, field) =>
                acc && value.includes(getFieldValue(field) ?? {}),
              true,
            );
          updatedValues = updatedValues.filter(includesAllFilters);
        }
        if (initialized) {
          // reset prop value
          onChange(null, { name: prop, value: "" });
        }
      }
      if (fixedValues) {
        // include them
        const values = getValues({ prop, values: fixedValues });
        updatedValues = updatedValues.concat(values);
      }
      if (nullValue) {
        // add last
        updatedValues = [...updatedValues, nullValue];
      }

      if (initialized) {
        // set the component state
        setValues(updatedValues);
      }

      return updatedValues;
    },
    [model, field, filterFields, onChange],
  );

  const [isActive, setIsActive] = useState(checkIfActive(false));

  const [values, setValues] = useState(populateValues(false));

  // hook to internal isValid state
  // useEffect(() => setIsValid(!!field.isValid), [field.isValid, setIsValid])

  useEffect(() => {
    if (activatedBy) {
      // recheck upon dependencies change
      model.onChange(
        Object.keys(activatedBy).map(resolvePath),
        () => checkIfActive(true) && populateValues(true),
        "active_deps:" + prop + ":" + groupId,
      );
    }
    if (filteredBy && !readonly) {
      // filter upon dependencies change
      model.onChange(
        filterFields,
        () => field.isActive && populateValues(true),
        "filter_deps:" + prop + ":" + groupId,
      );
    }
  }, [model]);

  useEffect(() => {
    // remove super admin roles from radio field if user is not bright/latham_admin, sales, or cust_serv
    if (
      type === "radio" &&
      values.includes(Role.BAI_ADMIN) &&
      user?.role !== Role.BAI_ADMIN &&
      user?.role !== Role.LATHAM_ADMIN &&
      user?.role !== Role.SALES &&
      user?.role !== Role.CUST_SERV
    ) {
      setValues(values.slice(0, -5));
    } else if (user?.role !== Role.BAI_ADMIN) {
      setValues(values.slice(0, -1));
    }

    // hide automatic statuses if user is not bright/latham_admin, sales, or cust_serv
    if (
      type === "radio" &&
      values.includes(ProjectStatus.ACTIVE) &&
      user?.role !== Role.BAI_ADMIN &&
      user?.role !== Role.LATHAM_ADMIN &&
      user?.role !== Role.SALES &&
      user?.role !== Role.CUST_SERV
    ) {
      setValues(values.slice(0, 2));
    } else if (values.includes(ProjectStatus.ACTIVE)) {
      setValues(values);
    }
  }, [type]);

  if (readonly) {
    input = (
      <ReadonlyField
        labelEl={labelEl}
        type={type}
        field={field}
        currentValue={currentValue}
        displayValue={getDisplayValue(currentValue)}
      />
    );
  } else {
    switch (type) {
      case "radio":
        input = (
          <Radio
            values={values}
            label={field.label}
            callbackfn={(value, i) => (
              <Form.Radio
                key={i}
                name={prop}
                label={getDisplayValue(value)}
                value={value}
                checked={value === currentValue}
                onChange={onChange}
              />
            )}
          />
        );
        break;

      case "select":
        input = (
          <Dropdown
            fluid
            selection
            name={prop}
            value={currentValue}
            placeholder={
              placeholder || t("component.field.chooseAnOption", "Choose an option")
            }
            options={values.map((value, i) => ({
              key: i,
              text: getDisplayValue(value),
              value,
            }))}
            onChange={onChange}
          />
        );
        break;

      case "grid":
        const { baseUrl, showLabels } = field;
        input = (
          <ImageGrid
            value={currentValue}
            items={values.map((value) => ({
              label: getDisplayValue(value),
              url: baseUrl + value,
              value,
            }))}
            onSelect={(item) => {
              onChange(null, { name: prop, value: item.value });
            }}
            placeholder={
              placeholder ||
              t("component.common.nothingToDisplay", "Nothing to display")
            }
            labelProp={showLabels ? "label" : ""}
            invalid={required && field.isValid === false}
          />
        );
        break;

      case "check":
        input = (
          <Checkbox
            name={prop}
            checked={currentValue}
            onChange={(e) => {
              onChange(e, { name: prop, value: e.target.checked });
            }}
            labelEl={labelEl}
          />
        );
        break;

      case "toggle":
        input = (
          <Toggle
            name={prop}
            isValid={currentValue}
            checked={currentValue}
            onChange={(e) => {
              onChange(e, { name: prop, value: e.target.checked });
            }}
          />
        );
        break;

      case "number":
        input = (
          <NumberInput
            model={model}
            field={field}
            onChange={onChange}
            className={"field"}
          />
        );
        break;

      case "scanner image":
        input = <img style={{ width: "100%" }} src={field.url} />;
        break;

      case "no image":
        input = <>{t("component.common.noImageFound", "No image found.")}</>;
        break;

      case "notes":
        input = <>{field.notes}</>;
        break;

      case "text":
      case "email":
      case "tel":
      case "date":
      case "textarea":
      case "range":
      default:
        const Component = type === "textarea" ? Form.TextArea : Form.Input;
        input = (
          <Component
            type={type}
            name={prop}
            value={currentValue}
            placeholder={
              placeholder ||
              t("component.field.enterLabel", "Enter {{label}}...", {
                label: label.toLowerCase(),
              })
            }
            onChange={onChange}
          />
        );
        break;
    }
  }

  if (isActive) {
    return readonly ? (
      input
    ) : (
      <MutatableField
        key={prop}
        required={required}
        disabled={disabled}
        valid={field.isValid}
        currentValue={currentValue}
        activated={activated}
        type={type}
        labelEl={labelEl}
        name={prop}
        onChange={toggleOn}
        input={input}
      />
    );
  }
  return <></>;
}
